import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Modal,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import FileUploadIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import figLogo from "../../assets/fig_CROP.png"; // <-- Update path if needed

// IMPORTANT: Import your languages object
import languages from "../../data/languages.js"; // Adjust path if necessary

// Example: Base64-encoded placeholder key: "sk-example-key"
// Replace this string with your actual base64-encoded key
//const ENCODED_OPENAI_KEY = "c2stZXhhbXBsZS1rZXk=";
const OPENAI_API_KEY =
    "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";

const ChatContainer = styled(Box)(({ theme }) => ({
    width: "350px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#E3F2FD", // Light blue
    color: "#000000", // Dark text for contrast
    borderLeft: "1px solid #CCC",
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
    padding: "16px",
    borderBottom: "1px solid #CCC",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
}));

const Logo = styled("img")({
    width: "80px",
    height: "auto",
    objectFit: "contain",
});

const TitleText = styled(Typography)({
    fontWeight: "bold",
    color: "#000",
    textAlign: "center",
});

const ChatMessages = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: "auto",
    padding: "16px",
}));

const ChatBubble = styled(Paper)(({ isUser }) => ({
    padding: "10px 12px",
    borderRadius: "12px",
    maxWidth: "80%",
    color: isUser ? "#fff" : "#000",
    backgroundColor: isUser ? "#90CAF9" : "#FFFFFF",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    cursor: "default",
    display: "flex",
    alignItems: "center",
    gap: "8px",
}));

const ChatInputArea = styled(Box)(({ theme }) => ({
    padding: "16px",
    borderTop: "1px solid #CCC",
}));

const AImenuwrapperchat = ({ apiid, onClose }) => {
    const [pageData, setPageData] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [message, setMessage] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const chatRef = useRef(null);

    // For file upload
    const [selectedFile, setSelectedFile] = useState(null);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);

    // 1) State for Language + Dialect
    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const [selectedDialect, setSelectedDialect] = useState(
        languages.English[0] || ""
    );

    // Update dialect if the new language doesn't have the old dialect
    useEffect(() => {
        if (!languages[selectedLanguage].includes(selectedDialect)) {
            setSelectedDialect(languages[selectedLanguage][0]);
        }
    }, [selectedLanguage]);

    useEffect(() => {
        const fetchMenuData = async () => {
            const schoolName = "BushElem"; // Replace this with dynamic input or URL param
            if (!schoolName) {
                console.error("School name is required to fetch data.");
                return;
            }

            try {
                // Define the date range (current month)
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split("T")[0];

                // Fetch data from MealViewer API
                const response = await axios.get(
                    `http://localhost:3000/api/menu/${schoolName}?start=${firstDay}&end=${lastDay}`
                );

                const rawData = response.data;

                // Group menu data by date
                const groupedByDate = rawData.menu.reduce((acc, item) => {
                    acc[item.menuDate] = acc[item.menuDate] || [];
                    acc[item.menuDate].push({
                        mealService: item.mealService,
                        name: item.name,
                        allergens: item.allergens || "None",
                    });
                    return acc;
                }, {});

                setPageData({
                    schoolName: rawData.physicalLocation?.name || "Unknown School",
                    address: rawData.physicalLocation?.address || "Address not available",
                    groupedMenu: groupedByDate,
                });
            } catch (error) {
                console.error("Error fetching menu data:", error.message);
            }
        };

        fetchMenuData();
    }, []);

    useEffect(() => {
        // Clear context when this component unmounts or `apiid` changes
        const cleanup = () => {
            setPageData(null);
            setChatHistory([]);
        };

        return cleanup;
    }, [apiid]);

    // Auto-scroll to bottom whenever chatHistory changes
    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [chatHistory]);

    const handleSendMessage = async () => {
        if (!message.trim()) return;

        const userMessage = { sender: "user", message };
        setChatHistory((prev) => [...prev, userMessage]);
        setIsThinking(true);

        try {
            // Enhanced system prompt with language + dialect
            const systemMessage = `
                You are an AI assistant specialized in school lunch menus.
                You can provide parents and students with menu item details,
                help them plan monthly meals, address dietary concerns, 
                and give insights into nutritional health options.
                
                Language Preference: ${selectedLanguage} - ${selectedDialect}

                Additional page context:
                Title: ${pageData?.PageTitle || "N/A"}
                Description: ${pageData?.PageDescription || "N/A"}
                Instructions: ${pageData?.ai_prompt_instructions || "N/A"}
                Languages: ${pageData?.ai_languages || "N/A"}.
            `;

            const response = await axios.post(
                "https://api.openai.com/v1/chat/completions",
                {
                    model: "gpt-4",
                    messages: [
                        { role: "system", content: systemMessage },
                        { role: "user", content: message },
                    ],
                    max_tokens: 500,
                    temperature: 0.7,
                },
                {
                    headers: {
                        Authorization: `Bearer ${OPENAI_API_KEY}`,
                    },
                }
            );

            const botResponse = response.data.choices[0].message.content.trim();

            // ---- NEW: Log prompt + response to swancreate endpoint ----
            const usageTokens = response.data.usage?.total_tokens || 0;

            try {
                await axios.post("https://api.swancreate.com/api/llm/AI_PromptResponseLog", {
                    // The DB auto-generates apiid, so no need to send it:
                    SourcePageID: null,
                    // Store the page title in SourcePageURL (or any field you prefer):
                    SourcePageURL: pageData?.PageTitle || "N/A",
                    UserID: "00000000-0000-0000-0000-000000000001",
                    OrganizationID: "N/A",
                    // Hard-code 'figcopilot' as requested:
                    BotID: "figcopilot",
                    PromptText: message,
                    ResponseText: botResponse,
                    TokenAmount: usageTokens,
                    PromptDate: new Date().toISOString(),
                    ResponseDate: new Date().toISOString(),
                    StorageLocation: null,
                    IsAnalyzed: false,
                    MainCategory: null,
                    SubCategory: null,
                    ipaddress: null,
                });
            } catch (logError) {
                console.error("Error logging prompt/response:", logError);
            }
            // ---- END: Logging ----

            setChatHistory((prev) => [
                ...prev,
                { sender: "bot", message: botResponse },
            ]);
        } catch (error) {
            console.error("Error sending message:", error.message);
            setChatHistory((prev) => [
                ...prev,
                { sender: "bot", message: "Error processing your request." },
            ]);
        } finally {
            setMessage("");
            setIsThinking(false);
        }
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    // Handle file upload to OpenAI
    const handleFileUpload = async () => {
        if (!selectedFile) return;
        try {
            setIsThinking(true);

            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("purpose", "fine-tune");

            const response = await axios.post(
                "https://api.openai.com/v1/files",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${OPENAI_API_KEY}`,
                    },
                }
            );

            console.log("Upload success:", response.data);
            setSnackbarMessage(`File uploaded! File ID: ${response.data.id}`);
            setShowSnackbar(true);

            setSelectedFile(null);
        } catch (error) {
            console.error("Error uploading file:", error);
            setSnackbarMessage("File upload failed.");
            setShowSnackbar(true);
        } finally {
            setIsThinking(false);
        }
    };

    // Copy chat bubble text
    const handleCopyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarMessage("Message copied to clipboard!");
            setShowSnackbar(true);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const handleCloseSnackbar = () => {
        setShowSnackbar(false);
        setSnackbarMessage("");
    };

    return (
        <ChatContainer>
            {/* Header Section */}
            <HeaderContainer>
                <Logo src={figLogo} alt="FIG Logo" />
                <TitleText variant="h6">
                    {pageData?.PageTitle || "AI Sidebar"}
                </TitleText>

                {/* 2) Instructions for School Lunch Assistance */}
                <Typography
                    variant="body2"
                    sx={{ textAlign: "center", fontStyle: "italic" }}
                >
                    I�m an AI expert on school lunch menus. I can help you:
                    <br />
                    - Find specific meal options you enjoy
                    <br />
                    - Plan your month�s meals
                    <br />
                    - Discuss dietary and health questions
                    <br />
                    - Provide info in various languages
                </Typography>

                {/* Language + Dialect Selectors (side by side) */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        gap: 1,
                        marginTop: 2,
                    }}
                >
                    {/* Main Language */}
                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel
                            id="language-select-label"
                            sx={{ color: "#00008B" }} // Dark blue text for label
                        >
                            Language
                        </InputLabel>
                        <Select
                            labelId="language-select-label"
                            value={selectedLanguage}
                            label="Language"
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                            sx={{
                                backgroundColor: "#FFFFFF", // White background
                                color: "#00008B", // Dark blue text for selected value
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#00008B", // Dark blue border
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#5B9BD5", // Lighter blue on hover
                                    },
                                },
                                "& .MuiSelect-icon": {
                                    color: "#00008B", // Dark blue for dropdown arrow
                                },
                            }}
                        >
                            {Object.keys(languages).map((lang) => (
                                <MenuItem
                                    key={lang}
                                    value={lang}
                                    sx={{
                                        color: "#00008B", // Dark blue text for items
                                        "&:hover": {
                                            backgroundColor: "#F1F1F1", // Light gray hover effect
                                        },
                                    }}
                                >
                                    {lang}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Dialect */}
                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel
                            id="dialect-select-label"
                            sx={{ color: "#00008B" }} // Dark blue text for label
                        >
                            Dialect
                        </InputLabel>
                        <Select
                            labelId="dialect-select-label"
                            value={selectedDialect}
                            label="Dialect"
                            onChange={(e) => setSelectedDialect(e.target.value)}
                            sx={{
                                backgroundColor: "#FFFFFF", // White background
                                color: "#00008B", // Dark blue text for selected value
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#00008B", // Dark blue border
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#5B9BD5", // Lighter blue on hover
                                    },
                                },
                                "& .MuiSelect-icon": {
                                    color: "#00008B", // Dark blue for dropdown arrow
                                },
                            }}
                        >
                            {languages[selectedLanguage].map((dialect) => (
                                <MenuItem
                                    key={dialect}
                                    value={dialect}
                                    sx={{
                                        color: "#00008B", // Dark blue text for items
                                        "&:hover": {
                                            backgroundColor: "#F1F1F1", // Light gray hover effect
                                        },
                                    }}
                                >
                                    {dialect}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

            </HeaderContainer>

            {/* Chat Messages */}
            <ChatMessages ref={chatRef}>
                {chatHistory.map((chat, idx) => {
                    const isUser = chat.sender === "user";
                    return (
                        <Box
                            key={idx}
                            sx={{
                                display: "flex",
                                justifyContent: isUser ? "flex-end" : "flex-start",
                                mb: 1.5,
                            }}
                        >
                            <ChatBubble isUser={isUser}>
                                <Box sx={{ flex: 1 }}>{chat.message}</Box>
                                {/* Copy Button */}
                                <Tooltip title="Copy to clipboard">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopyText(chat.message)}
                                        sx={{
                                            color: isUser ? "#fff" : "#000",
                                            "&:hover": {
                                                opacity: 0.7,
                                            },
                                        }}
                                    >
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </ChatBubble>
                        </Box>
                    );
                })}
            </ChatMessages>

            {/* File Upload + Input + Send */}
            <ChatInputArea>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<FileUploadIcon />}
                        sx={{
                            color: "#000",
                            borderColor: "#000",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.05)",
                            },
                        }}
                    >
                        {selectedFile ? selectedFile.name : "Upload File"}
                        <input type="file" hidden onChange={handleFileSelect} />
                    </Button>
                    {selectedFile && (
                        <Button
                            variant="contained"
                            onClick={handleFileUpload}
                            sx={{
                                backgroundColor: "#0288D1",
                                color: "#fff",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "#0277BD",
                                },
                            }}
                        >
                            Submit
                        </Button>
                    )}
                </Box>

                <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2} // multiline for word wrap
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    placeholder="Type your message..."
                    sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#FFFFFF", // White background
                            fontWeight: "bold", // Bold text
                            color: "#00008B", // Dark blue text
                            "& input, & textarea": {
                                fontWeight: "bold",
                                color: "#00008B",
                            },
                        },
                    }}
                />

                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSendMessage}
                    sx={{
                        backgroundColor: "#0288D1",
                        color: "#fff",
                        textTransform: "none",
                        "&:hover": {
                            backgroundColor: "#0277BD",
                        },
                    }}
                >
                    Send
                </Button>
            </ChatInputArea>

            {/* Thinking Overlay */}
            <Modal open={isThinking}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >
                    <CircularProgress sx={{ color: "#FFFFFF" }} />
                </Box>
            </Modal>

            {/* Snackbar for copy/upload messages */}
            <Snackbar
                open={showSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="info"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </ChatContainer>
    );
};

export default AImenuwrapperchat;
